/**
 * Google Identity Services — the ID-token flow (INV-8).
 *
 * GIS renders the sign-in button and hands us a signed JWT in a **JavaScript
 * callback**. Nothing navigates, nothing pops up, nothing redirects. That matters
 * because on iOS an installed PWA and Safari have separate storage jars: a
 * redirect flow can be serviced in Safari, whose callback writes the session
 * somewhere this app cannot read, and the user returns silently signed out with
 * no error to debug.
 *
 * **Never replace this with a redirect flow.** That is the bug.
 */

const GIS_SRC = "https://accounts.google.com/gsi/client";

/** The slice of the GIS surface we use. Declared here to avoid a types dependency. */
interface GoogleIdConfiguration {
  client_id: string;
  callback: (response: { credential: string }) => void;
  nonce?: string;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
  use_fedcm_for_prompt?: boolean;
}

interface GsiButtonConfiguration {
  type?: "standard" | "icon";
  theme?: "outline" | "filled_blue" | "filled_black";
  size?: "small" | "medium" | "large";
  text?: "signin_with" | "signup_with" | "continue_with";
  shape?: "rectangular" | "pill";
  logo_alignment?: "left" | "center";
  width?: number;
}

interface GoogleAccountsId {
  initialize: (config: GoogleIdConfiguration) => void;
  renderButton: (parent: HTMLElement, options: GsiButtonConfiguration) => void;
  disableAutoSelect: () => void;
}

declare global {
  interface Window {
    google?: { accounts: { id: GoogleAccountsId } };
  }
}

let loader: Promise<GoogleAccountsId> | null = null;

/** Load the GIS client once. Rejects loudly if it cannot load (INV-7). */
export function loadGoogleIdentity(): Promise<GoogleAccountsId> {
  if (loader) return loader;

  loader = new Promise<GoogleAccountsId>((resolve, reject) => {
    const existing = window.google?.accounts.id;
    if (existing) {
      resolve(existing);
      return;
    }

    const script = document.createElement("script");
    script.src = GIS_SRC;
    script.async = true;
    script.onload = () => {
      const api = window.google?.accounts.id;
      if (!api) {
        reject(new Error("Google sign-in loaded but exposed no API."));
        return;
      }
      resolve(api);
    };
    script.onerror = () =>
      reject(
        new Error(
          "Couldn't reach Google sign-in. Check your connection and try again.",
        ),
      );
    document.head.appendChild(script);
  });

  // A failed load must not be cached as a permanent failure — the user may
  // simply have been offline, and a retry should be allowed to succeed.
  loader.catch(() => {
    loader = null;
  });

  return loader;
}

export interface Nonce {
  /** Sent to the Worker alongside the credential. */
  raw: string;
  /** Passed to GIS. Google embeds it in the JWT; the Worker verifies the pair. */
  hashed: string;
}

/**
 * Generate a nonce pair.
 *
 * Google receives the SHA-256 hash and puts it in the token; the Worker
 * receives the raw value and checks that hashing it reproduces the claim. That binding is
 * what stops a token captured elsewhere being replayed against this app.
 */
export async function createNonce(): Promise<Nonce> {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const raw = btoa(String.fromCharCode(...bytes));
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(raw));
  const hashed = [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return { raw, hashed };
}

export function disableAutoSelect(): void {
  window.google?.accounts.id.disableAutoSelect();
}
